import {
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconChartBar,
  IconClock,
  IconDownload,
  IconFilter,
  IconRefresh,
  IconUsers,
  IconX,
} from '@tabler/icons';
import { DataTable } from 'mantine-datatable';
import { useCallback, useState } from 'react';

import { CustomDatePicker } from '../../components/custom-date-picker';
import { Page } from '../../components/page';
import {
  EpiReportComplianceRow,
  EpiReportDeliveryRow,
  EpiReportFilters,
  EpiReportLifecycleRow,
  EpiReportResult,
  fetchEpiReportsRequest,
} from '../../data/employees';
import { useFetch } from '../../hooks/useFetch';
import { EpiDeliveryReasonLabel } from '../../models/employees';
import { formatLocale } from '../../providers/dayjs-plugins';
import { errorNotification } from '../../providers/mantine-notifications';
import { SECTOR_OPTIONS } from '../../utils/constants/sectors';
import { exportEpiReportToExcel } from '../../utils/export-epi-report-excel';

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos os status' },
  { value: 'ok', label: 'Em dia' },
  { value: 'pendente', label: 'Pendente' },
  { value: 'vencido', label: 'Vencido' },
];

const STATUS_COLOR: Record<string, string> = {
  ok: 'green',
  pendente: 'yellow',
  vencido: 'red',
  alerta: 'orange',
  'sem-prazo': 'gray',
};

const STATUS_LABEL: Record<string, string> = {
  ok: 'Em dia',
  pendente: 'Pendente',
  vencido: 'Vencido',
  alerta: 'Alerta',
  'sem-prazo': 'Sem prazo',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge color={STATUS_COLOR[status] ?? 'gray'} size="sm">
      {STATUS_LABEL[status] ?? status}
    </Badge>
  );
}

function KpiCard({
  label,
  value,
  color,
  icon,
}: {
  label: string;
  value: number;
  color: string;
  icon: React.ReactNode;
}) {
  return (
    <Paper p="md" radius="sm" withBorder>
      <Group position="apart" noWrap>
        <Stack spacing={2}>
          <Text size="xs" color="dimmed" tt="uppercase" fw={600}>
            {label}
          </Text>
          <Text size="xl" fw={700} color={color}>
            {value}
          </Text>
        </Stack>
        <Box
          sx={{
            color: `var(--mantine-color-${color}-6)`,
            opacity: 0.4,
          }}
        >
          {icon}
        </Box>
      </Group>
    </Paper>
  );
}

export function EpiReports() {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [sector, setSector] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>('all');

  const {
    fetchData,
    state: { data: report, loading },
  } = useFetch<EpiReportResult>();

  const fetch = useCallback(
    (overrides?: Partial<EpiReportFilters>) => {
      const filters: EpiReportFilters = {
        startDate:
          overrides?.startDate ?? startDate
            ? overrides?.startDate ?? startDate?.toISOString().slice(0, 10)
            : undefined,
        endDate:
          overrides?.endDate ?? endDate
            ? overrides?.endDate ?? endDate?.toISOString().slice(0, 10)
            : undefined,
        sector: overrides?.sector ?? sector ?? undefined,
        status: (overrides?.status ??
          (status !== 'all' ? status : undefined)) as any,
      };
      fetchData({
        fetcher: () => fetchEpiReportsRequest(filters),
        onError: () =>
          errorNotification({
            title: 'Erro ao carregar relatório.',
            message: 'Tente novamente.',
          }),
      });
    },
    [startDate, endDate, sector, status],
  );

  function handleClearFilters() {
    setStartDate(null);
    setEndDate(null);
    setSector(null);
    setStatus('all');
  }

  function handleExportExcel() {
    if (!report) return;
    exportEpiReportToExcel({
      deliveries: report.deliveries,
      compliance: report.compliance,
      lifecycle: report.lifecycle,
      summary: report.summary,
      filters: {
        startDate: startDate?.toISOString().slice(0, 10),
        endDate: endDate?.toISOString().slice(0, 10),
      },
    });
  }

  const kpis = report?.kpis ?? {
    totalDeliveries: 0,
    totalEmployeesServed: 0,
    pendingCompliance: 0,
    needsReplacement: 0,
  };

  const deliveries = report?.deliveries ?? [];
  const compliance = report?.compliance ?? [];
  const lifecycle = report?.lifecycle ?? [];
  const summary = report?.summary ?? [];

  return (
    <Page>
      <Flex justify="space-between" align="center" mb={16}>
        <Title order={3}>Relatórios de EPI</Title>
        <Group spacing={8}>
          <Button
            variant="outline"
            leftIcon={<IconDownload size={16} />}
            onClick={handleExportExcel}
            disabled={!report}
          >
            Exportar Excel
          </Button>
        </Group>
      </Flex>

      {/* ── Filter bar ── */}
      <Paper p="md" radius="sm" withBorder mb={16}>
        <SimpleGrid cols={5} breakpoints={[{ maxWidth: 'md', cols: 2 }]}>
          <CustomDatePicker
            label="Data inicial"
            placeholder="de"
            clearable
            mb={0}
            value={startDate}
            onChange={setStartDate}
          />
          <CustomDatePicker
            label="Data final"
            placeholder="até"
            clearable
            mb={0}
            value={endDate}
            onChange={setEndDate}
          />
          <Select
            label="Setor"
            placeholder="todos"
            searchable
            clearable
            data={SECTOR_OPTIONS}
            mb={0}
            value={sector}
            onChange={setSector}
          />
          <Select
            label="Status"
            data={STATUS_OPTIONS}
            mb={0}
            value={status}
            onChange={setStatus}
          />
          <Flex align="flex-end" gap={8} style={{ height: '100%' }}>
            <Button
              variant="outline"
              leftIcon={<IconX size={14} />}
              onClick={handleClearFilters}
              style={{ flex: 1 }}
            >
              Limpar
            </Button>
            <Button
              leftIcon={<IconRefresh size={16} />}
              onClick={() => fetch()}
              loading={loading}
              style={{ flex: 1 }}
            >
              Atualizar
            </Button>
          </Flex>
        </SimpleGrid>
      </Paper>

      {/* ── KPI cards ── */}
      <SimpleGrid cols={4} breakpoints={[{ maxWidth: 'sm', cols: 2 }]} mb={20}>
        <KpiCard
          label="Total Entregue"
          value={kpis.totalDeliveries}
          color="blue"
          icon={<IconChartBar size={32} />}
        />
        <KpiCard
          label="Funcionários Atendidos"
          value={kpis.totalEmployeesServed}
          color="teal"
          icon={<IconUsers size={32} />}
        />
        <KpiCard
          label="Pendências de Matriz"
          value={kpis.pendingCompliance}
          color={kpis.pendingCompliance > 0 ? 'orange' : 'green'}
          icon={<IconAlertTriangle size={32} />}
        />
        <KpiCard
          label="Necessitam Reposição"
          value={kpis.needsReplacement}
          color={kpis.needsReplacement > 0 ? 'red' : 'green'}
          icon={<IconClock size={32} />}
        />
      </SimpleGrid>

      {!report && !loading && (
        <Paper p={40} radius="sm" withBorder>
          <Stack align="center" spacing={8}>
            <IconFilter size={36} color="gray" />
            <Text color="dimmed">
              Aplique filtros e clique em <b>Atualizar</b> para carregar o
              relatório.
            </Text>
          </Stack>
        </Paper>
      )}

      {(report || loading) && (
        <Tabs defaultValue="deliveries">
          <Tabs.List mb={16}>
            <Tabs.Tab value="deliveries">Histórico de Entregas</Tabs.Tab>
            <Tabs.Tab
              value="compliance"
              color={kpis.pendingCompliance > 0 ? 'orange' : undefined}
            >
              Conformidade &amp; Pendências
              {kpis.pendingCompliance > 0 && (
                <Badge color="orange" size="xs" variant="filled" ml={6}>
                  {kpis.pendingCompliance}
                </Badge>
              )}
            </Tabs.Tab>
            <Tabs.Tab value="lifecycle">Ciclo de Vida</Tabs.Tab>
            <Tabs.Tab value="summary">Consolidado por EPI</Tabs.Tab>
          </Tabs.List>

          {/* ── Aba 1: Histórico de Entregas ── */}
          <Tabs.Panel value="deliveries">
            <DataTable
              minHeight={200}
              withBorder
              borderRadius="sm"
              striped
              highlightOnHover
              fetching={loading}
              noRecordsText="Nenhuma entrega encontrada"
              records={deliveries}
              columns={[
                {
                  accessor: 'date',
                  title: 'Data',
                  render: (r: EpiReportDeliveryRow) =>
                    formatLocale(r.date, 'DD/MM/YYYY'),
                },
                { accessor: 'employeeName', title: 'Funcionário' },
                {
                  accessor: 'sector',
                  title: 'Setor',
                  render: (r: EpiReportDeliveryRow) => r.sector ?? '—',
                },
                { accessor: 'epiName', title: 'EPI' },
                {
                  accessor: 'ca',
                  title: 'CA',
                  render: (r: EpiReportDeliveryRow) => r.ca ?? '—',
                },
                { accessor: 'quantity', title: 'Qtd' },
                {
                  accessor: 'reason',
                  title: 'Motivo',
                  render: (r: EpiReportDeliveryRow) =>
                    r.reason
                      ? EpiDeliveryReasonLabel[r.reason] ?? r.reason
                      : '—',
                },
              ]}
            />
          </Tabs.Panel>

          {/* ── Aba 2: Conformidade ── */}
          <Tabs.Panel value="compliance">
            <DataTable
              minHeight={200}
              withBorder
              borderRadius="sm"
              striped
              highlightOnHover
              fetching={loading}
              noRecordsText="Nenhum item encontrado"
              records={compliance}
              columns={[
                { accessor: 'employeeName', title: 'Funcionário' },
                {
                  accessor: 'sector',
                  title: 'Setor',
                  render: (r: EpiReportComplianceRow) => r.sector ?? '—',
                },
                { accessor: 'epiName', title: 'EPI Obrigatório' },
                {
                  accessor: 'ca',
                  title: 'CA',
                  render: (r: EpiReportComplianceRow) => r.ca ?? '—',
                },
                {
                  accessor: 'lastDeliveredAt',
                  title: 'Última Entrega',
                  render: (r: EpiReportComplianceRow) =>
                    r.lastDeliveredAt
                      ? formatLocale(r.lastDeliveredAt, 'DD/MM/YYYY')
                      : '—',
                },
                {
                  accessor: 'status',
                  title: 'Status',
                  render: (r: EpiReportComplianceRow) => (
                    <StatusBadge status={r.status} />
                  ),
                },
              ]}
            />
          </Tabs.Panel>

          {/* ── Aba 3: Ciclo de Vida ── */}
          <Tabs.Panel value="lifecycle">
            <DataTable
              minHeight={200}
              withBorder
              borderRadius="sm"
              striped
              highlightOnHover
              fetching={loading}
              noRecordsText="Nenhum item encontrado"
              records={lifecycle}
              columns={[
                { accessor: 'employeeName', title: 'Funcionário' },
                {
                  accessor: 'sector',
                  title: 'Setor',
                  render: (r: EpiReportLifecycleRow) => r.sector ?? '—',
                },
                { accessor: 'epiName', title: 'EPI' },
                {
                  accessor: 'lastDeliveredAt',
                  title: 'Última Entrega',
                  render: (r: EpiReportLifecycleRow) =>
                    r.lastDeliveredAt
                      ? formatLocale(r.lastDeliveredAt, 'DD/MM/YYYY')
                      : '—',
                },
                {
                  accessor: 'daysSince',
                  title: 'Dias Decorridos',
                  render: (r: EpiReportLifecycleRow) =>
                    r.daysSince !== null ? `${r.daysSince}d` : '—',
                },
                {
                  accessor: 'nextDueDate',
                  title: 'Previsão Troca',
                  render: (r: EpiReportLifecycleRow) =>
                    r.nextDueDate
                      ? formatLocale(r.nextDueDate, 'DD/MM/YYYY')
                      : '—',
                },
                {
                  accessor: 'daysUntilDue',
                  title: 'Dias p/ Vencer',
                  render: (r: EpiReportLifecycleRow) =>
                    r.daysUntilDue !== null ? (
                      <Text
                        size="sm"
                        color={
                          r.daysUntilDue < 0
                            ? 'red'
                            : r.daysUntilDue <= 15
                            ? 'orange'
                            : 'inherit'
                        }
                        fw={r.daysUntilDue <= 15 ? 600 : undefined}
                      >
                        {r.daysUntilDue < 0
                          ? `${Math.abs(r.daysUntilDue)}d atraso`
                          : `${r.daysUntilDue}d`}
                      </Text>
                    ) : (
                      '—'
                    ),
                },
                {
                  accessor: 'status',
                  title: 'Status',
                  render: (r: EpiReportLifecycleRow) => (
                    <StatusBadge status={r.status} />
                  ),
                },
              ]}
            />
          </Tabs.Panel>

          {/* ── Aba 4: Consolidado por EPI ── */}
          <Tabs.Panel value="summary">
            <DataTable
              minHeight={200}
              withBorder
              borderRadius="sm"
              striped
              highlightOnHover
              fetching={loading}
              noRecordsText="Nenhum dado encontrado"
              records={summary}
              columns={[
                { accessor: 'epiName', title: 'EPI' },
                {
                  accessor: 'ca',
                  title: 'CA',
                  render: (r: any) => r.ca ?? '—',
                },
                {
                  accessor: 'category',
                  title: 'Categoria',
                  render: (r: any) => r.category ?? '—',
                },
                {
                  accessor: 'totalDeliveries',
                  title: 'Total Entregas',
                  render: (r: any) => (
                    <Text size="sm" fw={600}>
                      {r.totalDeliveries}
                    </Text>
                  ),
                },
                {
                  accessor: 'totalEmployees',
                  title: 'Funcionários Atendidos',
                },
              ]}
            />
          </Tabs.Panel>
        </Tabs>
      )}
    </Page>
  );
}
